import { formatRelativeTime } from "@/lib/format";
import type { IncidentPolicyInfo } from "@/types";
import { useState } from "react";

interface PolicyAttributionPanelProps {
  policy: IncidentPolicyInfo;
}

const POLICY_STATUS_LABELS: Record<string, string> = {
  within_sla: "Within SLA",
  warning: "Warning",
  overdue: "Overdue",
  critical: "Critical",
  disabled: "Not Applicable",
  unknown: "Unknown",
  valid: "Valid",
  expiring_soon: "Expiring Soon",
  expiring_urgently: "Expiring Urgently",
  expired: "Expired",
  healthy: "Healthy",
  elevated: "Elevated",
};

function policyLabel(policyId: string): string {
  return policyId
    .split(/[._]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function policyStatusLabel(status: string): string {
  return POLICY_STATUS_LABELS[status] ?? policyLabel(status);
}

function shortRecommendation(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const text = raw.replace(/^Recommendation:\s*/i, "").trim();
  if (text.length > 72) return `${text.slice(0, 69).trimEnd()}…`;
  return text;
}

/**
 * Surfaces Policy Engine attribution — compact summary, details on expand.
 */
export function PolicyAttributionPanel({ policy }: PolicyAttributionPanelProps) {
  const [open, setOpen] = useState(false);
  const status = policyStatusLabel(policy.status);
  const recommendation = shortRecommendation(policy.recommendation);

  return (
    <section className="iw-section iw-policy">
      <button
        type="button"
        className="btn btn-ghost iw-evidence-toggle iw-intelligence__group-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">{open ? "▼" : "▶"}</span> Policy evaluation
        {!open ? (
          <span className="iw-intelligence__group-summary">
            {policyLabel(policy.policy)} · {status}
            {recommendation ? ` · ${recommendation}` : ""}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <p className="iw-section__copy iw-section__copy--muted">
            Created by the Policy Engine after evaluating VerificationResult(s) against an explicit
            operational policy.
          </p>
          <dl className="iw-header__meta">
            <div className="iw-header__meta-item">
              <dt>Policy</dt>
              <dd>{policyLabel(policy.policy)}</dd>
            </div>
            <div className="iw-header__meta-item">
              <dt>Policy status</dt>
              <dd>{status}</dd>
            </div>
            {policy.severity ? (
              <div className="iw-header__meta-item">
                <dt>Policy severity</dt>
                <dd>{policy.severity}</dd>
              </div>
            ) : null}
            {policy.confidence ? (
              <div className="iw-header__meta-item">
                <dt>Confidence</dt>
                <dd>{policy.confidence}</dd>
              </div>
            ) : null}
            <div className="iw-header__meta-item">
              <dt>Evaluated at</dt>
              <dd>{formatRelativeTime(policy.evaluatedAt)}</dd>
            </div>
          </dl>
          <div className="iw-policy__body">
            <p className="iw-intelligence__summary">
              <span className="iw-policy__label">Reason: </span>
              {policy.reason}
            </p>
            {recommendation ? (
              <p className="iw-section__copy">
                <span className="iw-policy__label">Recommendation: </span>
                {recommendation}
              </p>
            ) : null}
          </div>
          <p className="iw-section__copy iw-section__copy--muted">
            Thresholds and supporting VerificationResults appear in Evidence as policy input and
            verification rows.
          </p>
        </>
      ) : null}
    </section>
  );
}
